// 90mod Analytics — Behavioral Event Tracking + Frustration Detection
// Tracks how users interact with the app to improve UX and understand behavior.
// Events are batched and flushed every 10s or on page hide.
// Never breaks the app — all errors silently swallowed.

(function () {
  'use strict';

  // ── Session / Identity ──────────────────────────────────────────────────
  const _sessionId = (() => {
    let id = sessionStorage.getItem('mod_sid');
    if (!id) { id = crypto.randomUUID(); sessionStorage.setItem('mod_sid', id); }
    return id;
  })();

  const _sessionStart = Date.now();

  // Device flag — declared early: _trackPageView() runs on load, before the
  // PWA-detection block below, so this must exist first (avoids TDZ ReferenceError).
  const _isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

  // ── Event Queue ─────────────────────────────────────────────────────────
  const _queue = [];
  let _flushTimer = null;

  function track(event, meta = {}) {
    try {
      meta.ts = Date.now();
      meta.path = meta.path || location.pathname;
      _queue.push({ event, meta });
      if (!_flushTimer) _flushTimer = setTimeout(_flush, 10_000);

      const hot = ['page_view','module_open','feature_used','cta_click','error',
                   'invite_sent','task_created','project_created','payment',
                   'rage_click','session_thrash','frustration_spike',
                   'cognitive_overload','overstimulation_signal','deep_engagement'];
      if (hot.includes(event)) _flush();
    } catch (_) {}
  }

  async function _flush() {
    try {
      clearTimeout(_flushTimer);
      _flushTimer = null;
      if (_queue.length === 0) return;
      const batch = _queue.splice(0, 50);
      const token = document.querySelector('meta[name="csrf-token"]')?.content;
      await fetch('/api/v1/analytics/events', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': token || '' },
        body: JSON.stringify({ session_id: _sessionId, events: batch }),
        keepalive: true
      });
    } catch (_) {}
  }

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') _flush();
  });
  window.addEventListener('pagehide', _flush, { capture: true });

  // ── Page Views ──────────────────────────────────────────────────────────
  function _trackPageView() {
    track('page_view', {
      path: location.pathname,
      search: location.search,
      referrer: document.referrer || null,
      title: document.title,
      mobile: _isMobile,
      view: _viewportBucket()
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', _trackPageView);
  } else {
    _trackPageView();
  }

  document.addEventListener('turbo:load', _trackPageView);

  // ── Click / CTA Tracking ────────────────────────────────────────────────
  document.addEventListener('click', (e) => {
    try {
      const el = e.target.closest('[data-track], a[href], button');
      if (!el) return;

      const label = el.dataset.track
        || el.getAttribute('aria-label')
        || el.textContent?.trim().slice(0, 60)
        || el.tagName;

      track('click', {
        label,
        tag: el.tagName.toLowerCase(),
        href: el.getAttribute('href') || null,
        classes: el.className?.toString().slice(0, 100)
      });
    } catch (_) {}
  }, { passive: true });

  // ── Form Submissions ────────────────────────────────────────────────────
  document.addEventListener('submit', (e) => {
    try {
      const form = e.target;
      const action = form.action?.replace(location.origin, '') || form.getAttribute('action');
      track('form_submit', { action, method: form.method });
    } catch (_) {}
  }, { passive: true });

  // ── Scroll Depth ────────────────────────────────────────────────────────
  const _scrollMilestones = [25, 50, 75, 100];
  const _scrollTracked = new Set();

  document.addEventListener('scroll', () => {
    try {
      const doc = document.documentElement;
      const scrollHeight = Math.max(document.body.scrollHeight, doc.scrollHeight) - window.innerHeight;
      if (scrollHeight <= 0) return;
      const pct = Math.round((window.scrollY / scrollHeight) * 100);
      for (const m of _scrollMilestones) {
        const key = `${location.pathname}:${m}`;
        if (pct >= m && !_scrollTracked.has(key)) {
          _scrollTracked.add(key);
          track('scroll_depth', { pct: m });
        }
      }
    } catch (_) {}
  }, { passive: true });

  document.addEventListener('turbo:load', () => { _scrollTracked.clear(); });

  // ══════════════════════════════════════════════════════════════════════════
  // BEHAVIORAL INTELLIGENCE LAYER
  // ══════════════════════════════════════════════════════════════════════════

  // ── Rage Clicks ─────────────────────────────────────────────────────────
  const _taps = [];
  document.addEventListener('click', (e) => {
    try {
      _taps.push({ x: e.clientX, y: e.clientY, t: Date.now() });
      const cutoff = Date.now() - 2000;
      while (_taps.length > 0 && _taps[0].t < cutoff) _taps.shift();
      if (_taps.length >= 5) {
        const el = e.target.closest('[data-track], a, button, [role="button"], input, select') || e.target;
        track('rage_click', {
          count: _taps.length,
          element: _describeElement(el),
          element_tag: el.tagName?.toLowerCase()
        });
        _frustration.rage++;
        _taps.length = 0;
      }
    } catch (_) {}
  }, { passive: true });

  // ── Rage Scroll ─────────────────────────────────────────────────────────
  let _scrollEvents = [];
  document.addEventListener('scroll', () => {
    try {
      const now = Date.now();
      _scrollEvents.push({ y: window.scrollY, t: now });
      const cutoff = now - 1500;
      _scrollEvents = _scrollEvents.filter(s => s.t > cutoff);

      if (_scrollEvents.length >= 15) {
        let dirChanges = 0;
        for (let i = 2; i < _scrollEvents.length; i++) {
          const d1 = _scrollEvents[i-1].y - _scrollEvents[i-2].y;
          const d2 = _scrollEvents[i].y - _scrollEvents[i-1].y;
          if ((d1 > 0 && d2 < 0) || (d1 < 0 && d2 > 0)) dirChanges++;
        }
        if (dirChanges >= 4) {
          track('rage_scroll', { direction_changes: dirChanges });
          _frustration.rageScroll++;
          _scrollEvents = [];
        }
      }
    } catch (_) {}
  }, { passive: true });

  // ── Session Thrash ──────────────────────────────────────────────────────
  const _navHistory = [];
  document.addEventListener('turbo:load', () => {
    try {
      const now = Date.now();
      _navHistory.push({ path: location.pathname, t: now });
      const cutoff = now - 10000;
      while (_navHistory.length > 0 && _navHistory[0].t < cutoff) _navHistory.shift();

      if (_navHistory.length >= 5) {
        const paths = _navHistory.map(n => n.path);
        const unique = new Set(paths);
        if (unique.size <= 3 && paths.length >= 5) {
          track('session_thrash', {
            pages: Array.from(unique),
            nav_count: paths.length,
            window_seconds: 10
          });
          _frustration.thrash++;
          _navHistory.length = 0;
        }
      }
    } catch (_) {}
  });

  // ── Bounce Detection ────────────────────────────────────────────────────
  let _pageLoadTime = Date.now();
  let _currentPath = location.pathname;

  document.addEventListener('turbo:before-visit', () => {
    try {
      const elapsed = Math.round((Date.now() - _pageLoadTime) / 1000);
      if (elapsed <= 3) {
        track('bounce', { seconds: elapsed, from_path: _currentPath });
        _frustration.bounce++;
      } else if (elapsed > 2) {
        track('time_on_page', { seconds: elapsed });
      }
    } catch (_) {}
  });

  document.addEventListener('turbo:load', () => {
    _pageLoadTime = Date.now();
    _currentPath = location.pathname;
  });

  // ── Frustration Score ───────────────────────────────────────────────────
  const _frustration = { rage: 0, rageScroll: 0, thrash: 0, bounce: 0, errors: 0 };

  function _checkFrustration() {
    const score = (_frustration.rage * 30) +
                  (_frustration.rageScroll * 15) +
                  (_frustration.thrash * 25) +
                  (_frustration.bounce * 10) +
                  (_frustration.errors * 20);

    if (score >= 50) {
      track('frustration_spike', {
        score,
        rage_clicks: _frustration.rage,
        rage_scrolls: _frustration.rageScroll,
        thrash_events: _frustration.thrash,
        bounces: _frustration.bounce,
        errors: _frustration.errors,
        session_age_s: Math.round((Date.now() - _sessionStart) / 1000)
      });
      // Reset after emit to avoid flooding
      Object.keys(_frustration).forEach(k => _frustration[k] = 0);
    }
  }

  setInterval(_checkFrustration, 15000);

  // ── Module / Feature Engagement ─────────────────────────────────────────
  const _moduleMap = {
    '/tasks': 'tasks', '/projects': 'projects', '/deals': 'crm',
    '/contacts': 'crm', '/invoices': 'finance', '/quotes': 'finance',
    '/expenses': 'finance', '/employees': 'hr', '/jobs': 'hr',
    '/applicants': 'hr', '/inventory': 'inventory', '/products': 'inventory',
    '/tickets': 'support', '/calendar': 'calendar', '/docs': 'docs',
    '/reports': 'reports', '/dashboard': 'dashboard', '/settings': 'settings',
    '/campfire': 'campfire', '/boards': 'boards', '/automations': 'automations'
  };

  document.addEventListener('turbo:load', () => {
    try {
      const path = location.pathname;
      for (const [prefix, mod] of Object.entries(_moduleMap)) {
        if (path.startsWith(prefix)) {
          track('module_visit', { module: mod });
          break;
        }
      }
    } catch (_) {}
  });

  // ── JS Error Tracking ───────────────────────────────────────────────────
  window.addEventListener('error', (e) => {
    try {
      track('js_error', {
        msg: e.message?.slice(0, 200),
        src: e.filename?.replace(location.origin, ''),
        line: e.lineno
      });
      _frustration.errors++;
    } catch (_) {}
  });

  window.addEventListener('unhandledrejection', (e) => {
    try {
      track('js_error', {
        msg: String(e.reason)?.slice(0, 200),
        type: 'unhandledrejection'
      });
      _frustration.errors++;
    } catch (_) {}
  });

  // ── Session Heartbeat ────────────────────────────────────────────────────
  setInterval(() => {
    if (!document.hidden) {
      track('heartbeat', {
        session_age_s: Math.round((Date.now() - _sessionStart) / 1000)
      });
    }
  }, 60_000);

  // ── Session End Summary ──────────────────────────────────────────────────
  window.addEventListener('pagehide', () => {
    try {
      const duration = Math.round((Date.now() - _sessionStart) / 1000);
      if (duration > 5) {
        track('session_end', {
          duration_s: duration,
          pages_visited: _navHistory.length + 1,
          frustration_total: Object.values(_frustration).reduce((a, b) => a + b, 0)
        });
      }
    } catch (_) {}
  }, { capture: true });

  // ── Mobile PWA Detection ─────────────────────────────────────────────────
  const _isPWA = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone;
  // _isMobile is declared at the top (used by _trackPageView on load).

  function _viewportBucket() {
    const w = window.innerWidth;
    if (w < 640) return 'mobile';
    if (w < 1024) return 'tablet';
    return 'desktop';
  }

  if (_isPWA) {
    track('pwa_launch', { mobile: _isMobile });
  }

  // ── Element Description Helper ──────────────────────────────────────────
  function _describeElement(el) {
    if (!el) return 'unknown';
    const text = el.textContent?.trim().slice(0, 40);
    const aria = el.getAttribute('aria-label');
    const dataTrack = el.dataset?.track;
    return dataTrack || aria || text || el.tagName?.toLowerCase() || 'unknown';
  }

  // ══════════════════════════════════════════════════════════════════════════
  // OVERSTIMULATION / COGNITIVE LOAD DETECTION
  // ══════════════════════════════════════════════════════════════════════════

  const _cogLoad = {
    moduleSwaps: 0,        // rapid module changes (context switching)
    dataViewToggles: 0,    // table/board/calendar view flips
    tabSwitches: 0,        // browser tab focus changes
    scrollVelocitySpikes: 0,
    dwellDrops: 0,         // pages abandoned under 5s (not bounces — mid-session)
    expandCollapses: 0,    // repeatedly opening/closing panels
    lastModule: null,
    lastModuleTime: 0,
    pageEnterTime: Date.now()
  };

  // ── Rapid Module Switching (context-switching stress) ───────────────────
  document.addEventListener('turbo:load', () => {
    try {
      const path = location.pathname;
      let currentMod = null;
      for (const [prefix, mod] of Object.entries(_moduleMap)) {
        if (path.startsWith(prefix)) { currentMod = mod; break; }
      }
      if (!currentMod) return;

      const now = Date.now();
      if (_cogLoad.lastModule && _cogLoad.lastModule !== currentMod) {
        const gap = now - _cogLoad.lastModuleTime;
        if (gap < 8000) {
          _cogLoad.moduleSwaps++;
          if (_cogLoad.moduleSwaps >= 4) {
            track('overstimulation_signal', {
              type: 'rapid_context_switch',
              count: _cogLoad.moduleSwaps,
              session_age_s: Math.round((now - _sessionStart) / 1000)
            });
            _cogLoad.moduleSwaps = 0;
          }
        }
      }
      _cogLoad.lastModule = currentMod;
      _cogLoad.lastModuleTime = now;

      // Dwell-drop: left previous page in under 5s (mid-session, not first page)
      const prevDwell = now - _cogLoad.pageEnterTime;
      if (_cogLoad.pageEnterTime > _sessionStart + 5000 && prevDwell < 5000) {
        _cogLoad.dwellDrops++;
      }
      _cogLoad.pageEnterTime = now;
    } catch (_) {}
  });

  // ── Data View Toggle Fatigue ────────────────────────────────────────────
  document.addEventListener('click', (e) => {
    try {
      const el = e.target.closest('[data-view-mode], [data-action*="view"], .view-switcher button');
      if (!el) return;
      _cogLoad.dataViewToggles++;
      if (_cogLoad.dataViewToggles >= 6) {
        track('overstimulation_signal', {
          type: 'view_toggle_fatigue',
          count: _cogLoad.dataViewToggles,
          module: _cogLoad.lastModule
        });
        _cogLoad.dataViewToggles = 0;
      }
    } catch (_) {}
  }, { passive: true });

  // ── Tab Focus Thrash (alt-tabbing away repeatedly) ──────────────────────
  let _tabBlurCount = 0;
  let _tabBlurWindow = Date.now();

  document.addEventListener('visibilitychange', () => {
    try {
      if (document.visibilityState === 'hidden') {
        const now = Date.now();
        if (now - _tabBlurWindow < 30000) {
          _tabBlurCount++;
          if (_tabBlurCount >= 6) {
            track('overstimulation_signal', {
              type: 'tab_thrash',
              count: _tabBlurCount,
              window_seconds: 30
            });
            _tabBlurCount = 0;
            _tabBlurWindow = now;
          }
        } else {
          _tabBlurCount = 1;
          _tabBlurWindow = now;
        }
      }
    } catch (_) {}
  });

  // ── Scroll Velocity Spikes (frantic scanning) ──────────────────────────
  let _lastScrollY = window.scrollY;
  let _lastScrollTime = Date.now();

  document.addEventListener('scroll', () => {
    try {
      const now = Date.now();
      const dt = now - _lastScrollTime;
      if (dt < 50) return; // debounce
      const dy = Math.abs(window.scrollY - _lastScrollY);
      const velocity = dy / dt; // px per ms
      _lastScrollY = window.scrollY;
      _lastScrollTime = now;

      if (velocity > 8) { // very fast scroll
        _cogLoad.scrollVelocitySpikes++;
        if (_cogLoad.scrollVelocitySpikes >= 5) {
          track('overstimulation_signal', {
            type: 'frantic_scroll',
            count: _cogLoad.scrollVelocitySpikes
          });
          _cogLoad.scrollVelocitySpikes = 0;
        }
      }
    } catch (_) {}
  }, { passive: true });

  // ── Panel/Accordion Thrash ─────────────────────────────────────────────
  document.addEventListener('click', (e) => {
    try {
      const el = e.target.closest('[data-action*="toggle"], [data-action*="collapse"], details > summary');
      if (!el) return;
      _cogLoad.expandCollapses++;
      if (_cogLoad.expandCollapses >= 8) {
        track('overstimulation_signal', {
          type: 'panel_thrash',
          count: _cogLoad.expandCollapses
        });
        _cogLoad.expandCollapses = 0;
      }
    } catch (_) {}
  }, { passive: true });

  // ── Cognitive Load Score (composite) ────────────────────────────────────
  function _checkCognitiveLoad() {
    const score = (_cogLoad.moduleSwaps * 20) +
                  (_cogLoad.dataViewToggles * 10) +
                  (_cogLoad.dwellDrops * 15) +
                  (_cogLoad.scrollVelocitySpikes * 8) +
                  (_cogLoad.expandCollapses * 5) +
                  (_tabBlurCount * 12);

    if (score >= 60) {
      track('cognitive_overload', {
        score,
        module_swaps: _cogLoad.moduleSwaps,
        view_toggles: _cogLoad.dataViewToggles,
        dwell_drops: _cogLoad.dwellDrops,
        scroll_spikes: _cogLoad.scrollVelocitySpikes,
        panel_thrash: _cogLoad.expandCollapses,
        tab_thrash: _tabBlurCount,
        session_age_s: Math.round((Date.now() - _sessionStart) / 1000)
      });
      // Reset
      Object.keys(_cogLoad).forEach(k => {
        if (typeof _cogLoad[k] === 'number') _cogLoad[k] = 0;
      });
      _tabBlurCount = 0;
    }
  }

  setInterval(_checkCognitiveLoad, 20000);

  // ── Positive Engagement Signals ─────────────────────────────────────────
  let _steadyModuleTime = 0;
  let _steadyCheckModule = null;

  setInterval(() => {
    try {
      if (document.hidden) return;
      const path = location.pathname;
      let currentMod = null;
      for (const [prefix, mod] of Object.entries(_moduleMap)) {
        if (path.startsWith(prefix)) { currentMod = mod; break; }
      }
      if (currentMod && currentMod === _steadyCheckModule) {
        _steadyModuleTime += 30;
        if (_steadyModuleTime === 120) { // 2 minutes steady in one module
          track('deep_engagement', {
            module: currentMod,
            seconds: _steadyModuleTime,
            session_age_s: Math.round((Date.now() - _sessionStart) / 1000)
          });
        }
      } else {
        if (_steadyModuleTime >= 30 && _steadyCheckModule) {
          track('module_dwell', {
            module: _steadyCheckModule,
            seconds: _steadyModuleTime
          });
        }
        _steadyCheckModule = currentMod;
        _steadyModuleTime = 0;
      }
    } catch (_) {}
  }, 30000);

  // ══════════════════════════════════════════════════════════════════════════
  // DESIGN ENGAGEMENT SCORING
  // ══════════════════════════════════════════════════════════════════════════
  // Tracks interaction quality per component type and page layout.
  // Scores how well each design pattern performs in terms of actual usage.

  const _designEngagement = {
    // Per-page scores (reset on navigation)
    pageClicks: 0,
    pageForms: { started: 0, completed: 0 },
    pageModals: { opened: 0, completed: 0, dismissed: 0 },
    pageDrawers: { opened: 0, interacted: 0 },
    pageCards: { clicked: 0, seen: 0 },
    pageTables: { sorted: 0, filtered: 0, rowClicks: 0 },
    pageTabs: { switched: 0 },
    pageDropdowns: { opened: 0, selected: 0 },
    pageBadges: { clicked: 0 },
    pageEmptyStates: { ctaClicked: 0, seen: 0 },
    pageEnter: Date.now(),
    maxScroll: 0
  };

  function _resetPageEngagement() {
    _designEngagement.pageClicks = 0;
    _designEngagement.pageForms = { started: 0, completed: 0 };
    _designEngagement.pageModals = { opened: 0, completed: 0, dismissed: 0 };
    _designEngagement.pageDrawers = { opened: 0, interacted: 0 };
    _designEngagement.pageCards = { clicked: 0, seen: 0 };
    _designEngagement.pageTables = { sorted: 0, filtered: 0, rowClicks: 0 };
    _designEngagement.pageTabs = { switched: 0 };
    _designEngagement.pageDropdowns = { opened: 0, selected: 0 };
    _designEngagement.pageBadges = { clicked: 0 };
    _designEngagement.pageEmptyStates = { ctaClicked: 0, seen: 0 };
    _designEngagement.pageEnter = Date.now();
    _designEngagement.maxScroll = 0;
  }

  // ── Component Interaction Tracking ──────────────────────────────────────

  document.addEventListener('click', (e) => {
    try {
      _designEngagement.pageClicks++;
      const el = e.target;

      // Card clicks
      if (el.closest('.rounded-xl[class*="shadow"], .card, [data-component="card"]')) {
        _designEngagement.pageCards.clicked++;
      }

      // Table row clicks
      if (el.closest('tr[data-action], tr[class*="cursor"], tbody tr a, tbody tr[onclick]')) {
        _designEngagement.pageTables.rowClicks++;
      }

      // Table sort/filter
      if (el.closest('th[data-action*="sort"], [data-sort], .sort-header')) {
        _designEngagement.pageTables.sorted++;
      }
      if (el.closest('[data-action*="filter"], [data-filter], .filter-btn')) {
        _designEngagement.pageTables.filtered++;
      }

      // Tab switches
      if (el.closest('[role="tab"], [data-action*="tab"], .tab-link, [data-tabs-target]')) {
        _designEngagement.pageTabs.switched++;
      }

      // Badge clicks
      if (el.closest('.badge, [class*="rounded-full"][class*="text-"][class*="px-"]')) {
        _designEngagement.pageBadges.clicked++;
      }

      // Empty state CTA
      if (el.closest('[data-empty-state] a, [data-empty-state] button, .empty-state a, .empty-state button')) {
        _designEngagement.pageEmptyStates.ctaClicked++;
      }

      // Dropdown selection
      if (el.closest('[data-dropdown-target="menu"] a, [data-dropdown-target="menu"] button, .mod-dropdown a')) {
        _designEngagement.pageDropdowns.selected++;
      }
    } catch (_) {}
  }, { passive: true });

  // ── Modal/Drawer Tracking ───────────────────────────────────────────────

  const _modalObserver = new MutationObserver((mutations) => {
    try {
      for (const m of mutations) {
        for (const node of m.addedNodes) {
          if (node.nodeType !== 1) continue;
          if (node.matches?.('[role="dialog"], .modal, [data-modal-target]')) {
            _designEngagement.pageModals.opened++;
          }
          if (node.matches?.('[data-slide-panel], .slide-panel, [data-drawer]')) {
            _designEngagement.pageDrawers.opened++;
          }
        }
        for (const node of m.removedNodes) {
          if (node.nodeType !== 1) continue;
          if (node.matches?.('[role="dialog"], .modal, [data-modal-target]')) {
            const hadSubmit = node.querySelector('form');
            if (hadSubmit) {
              _designEngagement.pageModals.completed++;
            } else {
              _designEngagement.pageModals.dismissed++;
            }
          }
        }
      }
    } catch (_) {}
  });
  _modalObserver.observe(document.body, { childList: true, subtree: true });

  // ── Form Engagement ─────────────────────────────────────────────────────

  document.addEventListener('focus', (e) => {
    try {
      if (e.target.matches?.('input, textarea, select')) {
        const form = e.target.closest('form');
        if (form && !form._modTracked) {
          form._modTracked = true;
          _designEngagement.pageForms.started++;
        }
      }
    } catch (_) {}
  }, { capture: true, passive: true });

  document.addEventListener('submit', () => {
    try {
      _designEngagement.pageForms.completed++;
    } catch (_) {}
  }, { passive: true });

  // ── Dropdown open tracking ──────────────────────────────────────────────

  document.addEventListener('click', (e) => {
    try {
      if (e.target.closest('[data-action*="dropdown#toggle"], [data-action*="dropdown#open"]')) {
        _designEngagement.pageDropdowns.opened++;
      }
    } catch (_) {}
  }, { passive: true });

  // ── Scroll reach per page ───────────────────────────────────────────────

  document.addEventListener('scroll', () => {
    try {
      const doc = document.documentElement;
      const scrollH = Math.max(document.body.scrollHeight, doc.scrollHeight) - window.innerHeight;
      if (scrollH <= 0) return;
      const pct = Math.round((window.scrollY / scrollH) * 100);
      if (pct > _designEngagement.maxScroll) _designEngagement.maxScroll = pct;
    } catch (_) {}
  }, { passive: true });

  // ── Visible card counting (IntersectionObserver) ────────────────────────

  function _countVisibleCards() {
    try {
      const cards = document.querySelectorAll('.rounded-xl[class*="shadow"], .card, [data-component="card"]');
      _designEngagement.pageCards.seen = cards.length;
      const emptyStates = document.querySelectorAll('[data-empty-state], .empty-state');
      _designEngagement.pageEmptyStates.seen = emptyStates.length;
    } catch (_) {}
  }

  document.addEventListener('turbo:load', () => {
    setTimeout(_countVisibleCards, 500);
  });

  // ── Page Engagement Score (emitted on navigation) ───────────────────────

  function _computeDesignScore() {
    const d = _designEngagement;
    const dwell = Math.round((Date.now() - d.pageEnter) / 1000);

    // Skip very short visits
    if (dwell < 3) return null;

    // Component interaction scores (0-100 scale per category)
    const scores = {};

    // Card engagement: clicked / seen ratio
    if (d.pageCards.seen > 0) {
      scores.cards = Math.min(100, Math.round((d.pageCards.clicked / d.pageCards.seen) * 100));
    }

    // Form completion rate
    if (d.pageForms.started > 0) {
      scores.forms = Math.round((d.pageForms.completed / d.pageForms.started) * 100);
    }

    // Modal completion vs dismissal
    if (d.pageModals.opened > 0) {
      scores.modals = Math.round((d.pageModals.completed / d.pageModals.opened) * 100);
    }

    // Table engagement (sort + filter + row click activity)
    const tableActions = d.pageTables.sorted + d.pageTables.filtered + d.pageTables.rowClicks;
    if (tableActions > 0) {
      scores.tables = Math.min(100, tableActions * 15);
    }

    // Dropdown follow-through (opened vs selected)
    if (d.pageDropdowns.opened > 0) {
      scores.dropdowns = Math.min(100, Math.round((d.pageDropdowns.selected / d.pageDropdowns.opened) * 100));
    }

    // Scroll reach
    scores.scroll_reach = d.maxScroll;

    // Click density (clicks per minute)
    const cpm = dwell > 0 ? Math.round((d.pageClicks / dwell) * 60) : 0;
    scores.click_density = Math.min(100, cpm * 5);

    // Composite engagement score
    const scoreValues = Object.values(scores);
    const composite = scoreValues.length > 0
      ? Math.round(scoreValues.reduce((a, b) => a + b, 0) / scoreValues.length)
      : 0;

    return {
      composite,
      dwell_s: dwell,
      clicks: d.pageClicks,
      ...scores,
      forms_started: d.pageForms.started,
      forms_completed: d.pageForms.completed,
      modals_opened: d.pageModals.opened,
      modals_completed: d.pageModals.completed,
      modals_dismissed: d.pageModals.dismissed,
      drawers_opened: d.pageDrawers.opened,
      tabs_switched: d.pageTabs.switched,
      cards_clicked: d.pageCards.clicked,
      cards_seen: d.pageCards.seen,
      table_sorts: d.pageTables.sorted,
      table_filters: d.pageTables.filtered,
      table_row_clicks: d.pageTables.rowClicks,
      badges_clicked: d.pageBadges.clicked,
      empty_cta_clicks: d.pageEmptyStates.ctaClicked
    };
  }

  // Emit on page leave
  document.addEventListener('turbo:before-visit', () => {
    try {
      const score = _computeDesignScore();
      if (score && score.composite > 0) {
        track('design_engagement', {
          path: location.pathname,
          module: _cogLoad.lastModule,
          ...score
        });
      }
      _resetPageEngagement();
    } catch (_) {}
  });

  // Also emit on session end
  window.addEventListener('pagehide', () => {
    try {
      const score = _computeDesignScore();
      if (score && score.composite > 0) {
        track('design_engagement', {
          path: location.pathname,
          module: _cogLoad.lastModule,
          ...score
        });
      }
    } catch (_) {}
  }, { capture: true });

  // ── Click Density Heatmap (zone-based) ──────────────────────────────────
  // Divides viewport into a 4x4 grid, tracks click distribution per page.

  const _clickZones = new Array(16).fill(0);

  document.addEventListener('click', (e) => {
    try {
      const col = Math.min(3, Math.floor((e.clientX / window.innerWidth) * 4));
      const row = Math.min(3, Math.floor((e.clientY / window.innerHeight) * 4));
      _clickZones[row * 4 + col]++;
    } catch (_) {}
  }, { passive: true });

  document.addEventListener('turbo:before-visit', () => {
    try {
      const total = _clickZones.reduce((a, b) => a + b, 0);
      if (total >= 3) {
        track('click_heatmap', {
          path: location.pathname,
          zones: _clickZones.slice(),
          total
        });
      }
      _clickZones.fill(0);
    } catch (_) {}
  });

  // ── Feature Discovery Tracking ──────────────────────────────────────────
  // Tracks first-time interactions with key UI patterns per session.

  const _discovered = new Set();

  function _trackDiscovery(feature, el) {
    if (_discovered.has(feature)) return;
    _discovered.add(feature);
    track('feature_discovery', {
      feature,
      element: _describeElement(el),
      session_age_s: Math.round((Date.now() - _sessionStart) / 1000)
    });
  }

  document.addEventListener('click', (e) => {
    try {
      const el = e.target;
      if (el.closest('[data-action*="search"]') || el.closest('[data-command-palette]'))
        _trackDiscovery('search_palette', el);
      if (el.closest('[data-action*="theme#toggle"]'))
        _trackDiscovery('theme_toggle', el);
      if (el.closest('[data-action*="sidebar"]'))
        _trackDiscovery('sidebar_toggle', el);
      if (el.closest('[data-action*="notification"]'))
        _trackDiscovery('notifications', el);
      if (el.closest('[data-action*="dropdown#toggle"]'))
        _trackDiscovery('dropdown_menu', el);
      if (el.closest('[data-action*="view"]') || el.closest('.view-switcher'))
        _trackDiscovery('view_switcher', el);
      if (el.closest('[data-action*="filter"]'))
        _trackDiscovery('filters', el);
      if (el.closest('[data-action*="sort"]'))
        _trackDiscovery('table_sort', el);
      if (el.closest('[data-action*="bulk"]'))
        _trackDiscovery('bulk_actions', el);
      if (el.closest('[data-action*="export"]'))
        _trackDiscovery('export', el);
      if (el.closest('[data-action*="import"]'))
        _trackDiscovery('import', el);
      if (el.closest('[data-slide-panel] a, [data-slide-panel] button'))
        _trackDiscovery('slide_panel_interaction', el);
      if (el.closest('[data-action*="favorite"]'))
        _trackDiscovery('favorites', el);
      if (el.closest('[data-keyboard]') || el.closest('[data-action*="keyboard"]'))
        _trackDiscovery('keyboard_shortcuts', el);
    } catch (_) {}
  }, { passive: true });

  // ── Form Abandonment Tracking ───────────────────────────────────────────

  const _activeForms = new WeakMap();

  document.addEventListener('focus', (e) => {
    try {
      const input = e.target;
      if (!input.matches?.('input, textarea, select')) return;
      const form = input.closest('form');
      if (!form || _activeForms.has(form)) return;
      _activeForms.set(form, {
        startTime: Date.now(),
        path: location.pathname,
        action: form.action?.replace(location.origin, '') || form.getAttribute('action') || 'unknown',
        fieldsTotal: form.querySelectorAll('input, textarea, select').length,
        fieldsTouched: new Set()
      });
    } catch (_) {}
  }, { capture: true, passive: true });

  document.addEventListener('input', (e) => {
    try {
      const form = e.target.closest('form');
      if (!form) return;
      const data = _activeForms.get(form);
      if (data) data.fieldsTouched.add(e.target.name || e.target.id || 'field');
    } catch (_) {}
  }, { capture: true, passive: true });

  document.addEventListener('turbo:before-visit', () => {
    try {
      // Check if any tracked forms were NOT submitted (abandoned)
      document.querySelectorAll('form').forEach(form => {
        const data = _activeForms.get(form);
        if (!data) return;
        const elapsed = Math.round((Date.now() - data.startTime) / 1000);
        if (elapsed > 2 && data.fieldsTouched.size > 0) {
          track('form_abandonment', {
            action: data.action,
            fields_total: data.fieldsTotal,
            fields_touched: data.fieldsTouched.size,
            seconds: elapsed,
            from_path: data.path
          });
        }
      });
    } catch (_) {}
  });

  document.addEventListener('submit', (e) => {
    try {
      // Mark form as submitted so it's not counted as abandoned
      _activeForms.delete(e.target);
    } catch (_) {}
  }, { passive: true });

  // ══════════════════════════════════════════════════════════════════════════
  // COMPONENT INTERACTION TIMING
  // ══════════════════════════════════════════════════════════════════════════
  // How long users spend inside modals, drawers, dropdowns before closing.

  const _componentTimers = new WeakMap();

  function _startComponentTimer(el, type) {
    if (!el || _componentTimers.has(el)) return;
    _componentTimers.set(el, { type, opened: Date.now(), path: location.pathname });
  }

  function _endComponentTimer(el) {
    if (!el) return;
    const data = _componentTimers.get(el);
    if (!data) return;
    _componentTimers.delete(el);
    const ms = Date.now() - data.opened;
    if (ms < 300) return; // ignore instant open/close (animation artifacts)
    track('component_timing', {
      type: data.type,
      seconds: Math.round(ms / 1000),
      ms,
      path: data.path
    });
  }

  // Modal timing
  const _timingObserver = new MutationObserver((mutations) => {
    try {
      for (const m of mutations) {
        for (const node of m.addedNodes) {
          if (node.nodeType !== 1) continue;
          if (node.matches?.('[role="dialog"], .modal, [data-modal-target="container"]'))
            _startComponentTimer(node, 'modal');
          if (node.matches?.('[data-slide-panel], .slide-panel, [data-drawer]'))
            _startComponentTimer(node, 'drawer');
          // Also check children (turbo-frame insertions)
          node.querySelectorAll?.('[role="dialog"], .modal, [data-slide-panel], .slide-panel').forEach(child => {
            const t = child.matches('[role="dialog"], .modal') ? 'modal' : 'drawer';
            _startComponentTimer(child, t);
          });
        }
        for (const node of m.removedNodes) {
          if (node.nodeType !== 1) continue;
          _endComponentTimer(node);
          node.querySelectorAll?.('[role="dialog"], .modal, [data-slide-panel], .slide-panel').forEach(_endComponentTimer);
        }
      }
    } catch (_) {}
  });
  _timingObserver.observe(document.body, { childList: true, subtree: true });

  // Dropdown timing
  document.addEventListener('click', (e) => {
    try {
      const trigger = e.target.closest('[data-action*="dropdown#toggle"]');
      if (!trigger) return;
      const wrapper = trigger.closest('[data-controller*="dropdown"]');
      if (!wrapper) return;
      const menu = wrapper.querySelector('[data-dropdown-target="menu"]');
      if (!menu) return;
      if (menu.classList.contains('hidden')) {
        _startComponentTimer(menu, 'dropdown');
      } else {
        _endComponentTimer(menu);
      }
    } catch (_) {}
  }, { passive: true });

  // Catch dropdowns closed by clicking outside
  document.addEventListener('click', (e) => {
    try {
      if (e.target.closest('[data-controller*="dropdown"]')) return;
      document.querySelectorAll('[data-dropdown-target="menu"]:not(.hidden)').forEach(menu => {
        _endComponentTimer(menu);
      });
    } catch (_) {}
  }, { passive: true });

  // ── Export for manual tracking ───────────────────────────────────────────
  window.modTrack = track;
})();
